import { Link, useForm } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BusinessRef } from '@/types'

type Props = {
  business: BusinessRef
}

export default function BackofficeBusinessPhysicalMailsNew({ business }: Props) {
  const form = useForm({ sender: '', subject: '' })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.post(`/backoffice/businesses/${business.id}/physical_mails`)
  }

  return (
    <BackofficeLayout
      title="Upload Physical Mail"
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Businesses', href: '/backoffice/businesses' },
        { label: business.name, href: `/backoffice/businesses/${business.id}` },
        { label: 'Physical Mail', href: `/backoffice/businesses/${business.id}/physical_mails` },
        { label: 'Upload' },
      ]}
    >
      <div className="max-w-md space-y-6">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Upload Physical Mail</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Step 1 of 2: Enter mail details for <span className="font-medium text-foreground">{business.name}</span>
          </p>
        </div>

        <section className="border rounded-lg">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Mail details</h2>
          </div>
          <form onSubmit={handleSubmit} className="px-4 py-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="sender">
                Sender <span className="text-destructive">*</span>
              </Label>
              <Input
                id="sender"
                value={form.data.sender}
                onChange={(e) => form.setData('sender', e.target.value)}
                placeholder="e.g. Companies House"
              />
              {form.errors.sender && (
                <p className="text-sm text-destructive">{form.errors.sender}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={form.data.subject}
                onChange={(e) => form.setData('subject', e.target.value)}
                placeholder="e.g. Annual Confirmation Statement"
              />
              {form.errors.subject && (
                <p className="text-sm text-destructive">{form.errors.subject}</p>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button type="button" variant="outline" asChild>
                <Link href={`/backoffice/businesses/${business.id}/physical_mails`}>Cancel</Link>
              </Button>
              <Button type="submit" disabled={form.processing}>
                {form.processing ? 'Creating…' : 'Create & upload scan →'}
              </Button>
            </div>
          </form>
        </section>

        <p className="text-xs text-muted-foreground">
          After creating the record you'll be able to upload the scan on the next page.
        </p>
      </div>
    </BackofficeLayout>
  )
}
